import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type CopySettingsUpdatedEventProps } from '@/models/analytics/features/sessions';
import {
  trackDeeplinkTriggeredSpec,
  trackExperimentStartedSpec,
  trackExportContentSavedSpec,
  trackExportSettingToggledSpec,
  trackExportUpdatedSpec,
  trackForceUpdateDeferredSpec,
  trackForceUpdateInitiatedSpec,
  trackForceUpdateViewedSpec,
  trackHaikuTabChangedSpec,
  trackHelpViewedSpec,
  trackNotificationUpdatedSpec,
  trackPersonalizationUpdatedSpec,
  trackScribeStyleUpdatedSpec,
  trackSettingsUpdatedSpec,
  trackTabSharingDoShowMessageAgainSpec,
  trackTabSharingNotAvailableModalShownSpec,
  trackVoiceStyleUpdatedSpec,
  type DeeplinkTriggered,
  type ExperimentStarted,
  type ExportContentSaved,
  type ExportSettingToggled,
  type ExportUpdated,
  type ForceUpdateDeferred,
  type ForceUpdateInitiated,
  type ForceUpdateViewed,
  type HaikuTabChanged,
  type HelpViewed,
  type NotificationUpdated,
  type PersonalizationUpdated,
  type ScribeStyleUpdated,
  type SettingsUpdated,
  type TabSharingDoShowMessageAgain,
  type TabSharingNotAvailableModalShown,
  type VoiceStyleUpdated,
} from '@snowtype/snowplow';
import type { SnowplowEventMap } from '../trackSnowplowEvent';

export const settingsEvents: SnowplowEventMap = {
  [TrackingEventName.SettingsUpdated]: (props) =>
    trackSettingsUpdatedSpec(props as SettingsUpdated),
  // Click-time intent from the copy-settings toggles (not confirmed backend
  // persistence), folded into the shared settings_updated schema rather than a
  // bespoke Snowplow event.
  [TrackingEventName.CopySettingsUpdated]: (props) => {
    const { setting, is_enabled, copy_settings } = props as CopySettingsUpdatedEventProps;
    const payload: SettingsUpdated = {
      update_action: 'copy_setting_toggled',
      setting,
      is_enabled,
      copy_settings: {
        headings: copy_settings.headings,
        bold_and_italics: copy_settings.bold_and_italics,
        bullet_points: copy_settings.bullet_points,
        tables: copy_settings.tables,
      },
    };
    return trackSettingsUpdatedSpec(payload);
  },
  [TrackingEventName.ExportSettingToggled]: (props) =>
    trackExportSettingToggledSpec(props as ExportSettingToggled),
  [TrackingEventName.ExportContentSaved]: (props) =>
    trackExportContentSavedSpec(props as ExportContentSaved),
  [TrackingEventName.ExportUpdated]: (props) =>
    trackExportUpdatedSpec(props as ExportUpdated),
  [TrackingEventName.NotificationUpdated]: (props) =>
    trackNotificationUpdatedSpec(props as NotificationUpdated),
  [TrackingEventName.ForceUpdateViewed]: (props) =>
    trackForceUpdateViewedSpec(props as ForceUpdateViewed),
  [TrackingEventName.ForceUpdateDeferred]: (props) =>
    trackForceUpdateDeferredSpec(props as ForceUpdateDeferred),
  [TrackingEventName.ForceUpdateInitiated]: (props) =>
    trackForceUpdateInitiatedSpec(props as ForceUpdateInitiated),
  [TrackingEventName.PersonalizationUpdated]: (props) =>
    trackPersonalizationUpdatedSpec(props as PersonalizationUpdated),
  [TrackingEventName.VoiceStyleUpdated]: (props) =>
    trackVoiceStyleUpdatedSpec(props as VoiceStyleUpdated),
  [TrackingEventName.HelpViewed]: (props) => trackHelpViewedSpec(props as HelpViewed),
  [TrackingEventName.DeeplinkTriggered]: (props) =>
    trackDeeplinkTriggeredSpec(props as DeeplinkTriggered),
  [TrackingEventName.HaikuTabChanged]: (props) =>
    trackHaikuTabChangedSpec(props as HaikuTabChanged),
  [TrackingEventName.TabSharingDoNoShowMessageAgain]: (props) =>
    trackTabSharingDoShowMessageAgainSpec(props as TabSharingDoShowMessageAgain),
  [TrackingEventName.TabSharingNotAvailableModalShown]: (props) =>
    trackTabSharingNotAvailableModalShownSpec(props as TabSharingNotAvailableModalShown),
  [TrackingEventName.ScribeStyleUpdated]: (props) =>
    trackScribeStyleUpdatedSpec(props as ScribeStyleUpdated),
  [TrackingEventName.ExperimentStarted]: (props) =>
    trackExperimentStartedSpec(props as ExperimentStarted),
};
